import axios from 'axios';
import { axiosInstance } from './axiosInstance';
import { ProjectsGetResponse } from '../types/project/ProjectGetResponse';
import { ProjectPostRequest } from '../types/project/ProjectPostRequest';
import { ProjectPutRequest } from '../types/project/ProjectPutRequest';
import { ProjectGetQueryParams } from '../types/project/ProjectGetQueryParams';
import { ManHoursGetResponse } from '../types/manhour/ManHourGetResponse';
import { ManHourGetQueryParams } from '../types/manhour/ManHourGetQueryParams';
import { CostsGetResponse } from '../types/cost/CostGetResponse';
import { CostPostRequest } from '../types/cost/CostPostRequest';
import { CostPutRequest } from '../types/cost/CostPutRequest';
import { EmployeesGetResponse } from '../types/employee/EmployeeGetResponse';
import { EmployeePostRequest } from '../types/employee/EmployeePostRequest';
import { EmployeePutRequest } from '../types/employee/EmployeePutRequest';
import { EmployeeGetQueryParams } from '../types/employee/EmployeeGetQueryParams';
import {
  ReportManagementsGetResponse,
  EmptyReportManagementsGetResponse,
} from '../types/reportsManagement/ReportManagementGetResponse';
import { ReportManagementGetQueryParams } from '../types/reportsManagement/ReportManagementGetQueryParams';
import type { Suggestion } from '../types/search/Suggestion';
import { BusinessReportsGetResponse } from '../types/businessReport/BusinessReportGetResponse';
import { BusinessReportGetQueryParams } from '../types/businessReport/BusinessReportGetQueryParams';
import {
  BusinessReportPutRequest,
  FormattedBusinessReportPutRequest,
} from '../types/businessReport/BusinessReportPutRequest';
import { AssignmentGetResponse } from '../types/assignment/AssignmentGetResponse';
import { AssignmentPostRequest } from '../types/assignment/AssignmentPostRequest';
import { SuggestionForBusinessReport } from '../types/search/SuggestionForBusinessReport';
import { BusinessReportPostRequest } from '../types/businessreport/BusinessReportPostRequest';
import { RateYearsGetResponse } from '../types/rate/RateYearsGetResponse';
import { RateGetResponse } from '../types/rate/RateGetResponse';
import { RatePutRequest } from '../types/rate/RatePutRequest';
import { WorkingtimeGetQueryParams } from '../types/workingtime/WorkingtimeGetQueryParams';
import { WorkingtimeGetResponse } from '../types/workingtime/WorkingtimeGetResponse';
import { CostAccountingGetResponse } from '../types/costAccounting/CostAccountingGetResponse';
import { CostAccountingGetQueryParams } from '../types/costAccounting/CostAccountingGetQueryParams';
import { CostAccountingDairenGetResponse } from '../types/costAccountingDairen/CostAccountingGetResponse';
import { CostAccountingDairenGetQueryParams } from '../types//costAccountingDairen/CostAccountingGetQueryParams';
import { UnitPriceYearsGerResponse } from '../types/unitprice/UnitPriceYearsGerResponse';
import { UnitPriceGetResponse } from '../types/unitprice/UnitPriceGetResponse';
import { FormattedUnitPricePutRequest } from '../types/unitprice/UnitPricePutRequest';
import { UnitPriceCsvExportParams } from '../types/unitprice/UnitPriceCsvExportParams';
import { AuthResponse } from '../types/auth/AuthResponse';
import { LoginRequest } from '../types/auth/LoginRequest';
import { UnitPriceGetQueryParams } from '../types/unitprice/UnitPriceGetQueryParams';

const url = process.env.REACT_APP_API_URL;
const api = axiosInstance;

/**
 * axiosInstanceを使わないAPI
 */
// ログイン
export const login = async (data: LoginRequest): Promise<AuthResponse> => {
  const response = await axios.post(`${url}/login`, data, {
    withCredentials: true,
  });
  return response.data;
};

// ログアウト
export const logoutRequest = async (): Promise<void> => {
  await axios.post(`${url}/logout`, {}, { withCredentials: true });
};

// アクセストークン再取得
export const refreshToken = async (): Promise<AuthResponse> => {
  const response = await axios.post(
    `${url}/refresh`,
    {},
    { withCredentials: true }
  );
  return response.data;
};

/**
 * 認証が必要なAPI
 */
export const getProjectList = async (
  queryParams: ProjectGetQueryParams
): Promise<ProjectsGetResponse> => {
  const response = await api.get('/admin/projects', {
    params: queryParams,
  });
  return response.data;
};

export const createProject = async (
  data: ProjectPostRequest
): Promise<void> => {
  await api.post('/admin/projects', data);
};

export const deleteProject = async (id: number): Promise<void> => {
  await api.delete(`/admin/projects/${id}`);
};

export const updateProject = async (
  id: number,
  data: ProjectPutRequest
): Promise<void> => {
  await api.put(`/admin/projects/${id}`, data);
};

// 業務報告管理
export const getReportManagementList = async (
  queryParams: ReportManagementGetQueryParams
): Promise<ReportManagementsGetResponse> => {
  const response = await api.get(`/admin/reports`, {
    params: queryParams,
  });
  return response.data;
};

export const exportReportManagementsCsv = async (
  queryParams: ReportManagementGetQueryParams
): Promise<{ blob: Blob; filename?: string }> => {
  const response = await api.get(`/admin/reports/managementcsv`, {
    params: queryParams,
    responseType: 'blob',
  });

  const cd = response.headers['content-disposition'] as string | undefined;
  const nameFromHeader =
    cd?.match(/filename\*=UTF-8''([^;]+)/)?.[1] ||
    cd?.match(/filename="?([^"]+)"?/)?.[1];

  return { blob: response.data as Blob, filename: nameFromHeader };
};

export const exportReportAccountingCsv = async (queryParams: {
  month?: string;
}): Promise<{ blob: Blob; filename?: string }> => {
  const response = await api.get(`/admin/reports/accountingcsv`, {
    params: queryParams,
    responseType: 'blob',
  });

  const cd = response.headers['content-disposition'] as string | undefined;
  const nameFromHeader =
    cd?.match(/filename\*=UTF-8''([^;]+)/)?.[1] ||
    cd?.match(/filename="?([^"]+)"?/)?.[1];

  return { blob: response.data as Blob, filename: nameFromHeader };
};

export const updateReportManagement = async (
  reportId: number,
  body: FormattedBusinessReportPutRequest
): Promise<void> => {
  await api.put(`/admin/reports/${reportId}`, body);
};

export const deleteReportManagement = async (
  reportId: number
): Promise<void> => {
  await api.delete(`/admin/reports/${reportId}`);
};

// 費用管理
export const getCostList = async (
  projectId: number
): Promise<CostsGetResponse> => {
  const response = await api.get(`/admin/projects/${projectId}/costs`);
  return response.data;
};

export const createCost = async (
  projectId: number,
  data: CostPostRequest
): Promise<void> => {
  await api.post(`/admin/projects/${projectId}/costs`, data);
};

export const deleteCost = async (
  projectId: number,
  costId: number
): Promise<void> => {
  await api.delete(`/admin/projects/${projectId}/costs/${costId}`);
};

export const updateCost = async (
  costId: number,
  projectId: number,
  data: CostPutRequest
): Promise<void> => {
  await api.put(`/admin/projects/${projectId}/costs/${costId}`, data);
};

// 社員関連API
export const getEmployeeList = async (
  queryParams: EmployeeGetQueryParams
): Promise<EmployeesGetResponse> => {
  const { data } = await api.get(`/admin/employees`, {
    params: queryParams,
  });
  return data;
};

export const createEmployee = async (
  data: EmployeePostRequest
): Promise<void> => {
  await api.post(`/admin/employees`, data);
};

export const updateEmployee = async (
  id: number,
  data: EmployeePutRequest
): Promise<void> => {
  await api.put(`/admin/employees/${id}`, data);
};

export const deleteEmployee = async (id: number): Promise<void> => {
  await api.delete(`/admin/employees/${id}`);
};

// 社員名検索候補取得
export const searchEmployeeSuggestions = async (
  q: string
): Promise<Suggestion[]> => {
  const response = await api.get(`/common/autocomplete/employees`, {
    params: { q },
  });
  return response.data;
};

// プロジェクト名検索候補取得
export const searchProjectSuggestions = async (
  q: string
): Promise<Suggestion[]> => {
  const response = await api.get(`/common/autocomplete/projects`, {
    params: { q },
  });
  return response.data;
};

// 業務報告一覧取得
export const getBusinessReports = async (
  params: BusinessReportGetQueryParams
): Promise<BusinessReportsGetResponse> => {
  const { data } = await api.get(`/user/reports`, {
    params,
  });
  return data;
};

// 業務報告の更新
export const updateBusinessReport = async (
  reportId: number,
  body: FormattedBusinessReportPutRequest
): Promise<void> => {
  await api.put(`/user/reports/${reportId}`, body);
};

// 業務報告の削除
export const deleteBusinessReport = async (reportId: number): Promise<void> => {
  await api.delete(`/user/reports/${reportId}`);
};

// 参画社員管理
export const getAssignments = async (
  projectId: number
): Promise<AssignmentGetResponse> => {
  const response = await api.get(`/admin/projects/${projectId}/assignments`);
  return response.data;
};

// 追加
export const createAssignment = async (
  projectId: number,
  data: AssignmentPostRequest
): Promise<void> => {
  await api.post(`/admin/projects/${projectId}/assignments`, data);
};

// 削除
export const deleteAssignment = async (id: number): Promise<void> => {
  await api.delete(`/admin/assignments/${id}`);
};

export const getManHourList = async (
  queryParams: ManHourGetQueryParams
): Promise<ManHoursGetResponse> => {
  const response = await api.get(`/admin/manhours`, {
    params: queryParams,
  });
  return response.data;
};

// プロジェクトナンバー検索候補取得
export const searchProjectNumberSuggestions = async (
  q: string
): Promise<SuggestionForBusinessReport[]> => {
  const response = await api.get(`/common/autocomplete/projects/number`, {
    params: { q },
  });
  return response.data;
};

// プロジェクトナンバー検索候補取得（全プロジェクト対象）
export const searchProjectNumbersFromAllProjects = async (
  q: string
): Promise<SuggestionForBusinessReport[]> => {
  const response = await api.get(`/common/autocomplete/projects/number/all`, {
    params: { q },
  });
  return response.data;
};

export const exportManHoursCsv = async (
  queryParams: ManHourGetQueryParams
): Promise<{ blob: Blob; filename?: string }> => {
  const response = await api.get(`/admin/manhours/csv`, {
    params: queryParams,
    responseType: 'blob',
  });

  const cd = response.headers['content-disposition'] as string | undefined;
  const nameFromHeader =
    cd?.match(/filename\*=UTF-8''([^;]+)/)?.[1] ||
    cd?.match(/filename="?([^"]+)"?/)?.[1];

  return { blob: response.data as Blob, filename: nameFromHeader };
};

// 業務報告送信
export const postBusinessReports = async (
  data: BusinessReportPostRequest
): Promise<void> => {
  await api.post(`/user/reports`, data);
};

// 業務報告一括送信
export const postBusinessReportsBulk = async (
  data: BusinessReportPostRequest[]
): Promise<void> => {
  await api.post(`/user/reports/bulk`, data);
};

//レート年プルダウン取得
export const getRateYears = async (): Promise<RateYearsGetResponse> => {
  const response = await api.get(`/admin/rates/periods`);
  return response.data;
};

// レートの期間追加
export const createRateYear = async (year: string): Promise<void> => {
  await api.post(`/admin/rates/periods`, { year });
};

// 指定した年のレート取得
export const getRatesByYearId = async (
  yearId: number
): Promise<RateGetResponse> => {
  const response = await api.get(`/admin/rates/${yearId}`);
  return response.data;
};

// レート更新
export const updateRates = async (
  yearId: number,
  data: RatePutRequest
): Promise<void> => {
  await api.put(`/admin/rates/${yearId}`, data);
};

// 稼働一覧取得
export const getWorkingtimeList = async (
  queryParams: WorkingtimeGetQueryParams
): Promise<WorkingtimeGetResponse> => {
  const response = await api.get(`/common/workingtimes`, {
    params: queryParams,
  });
  return response.data;
};

// 稼働CSV出力
export const exportWorkingtimeCsv = async (
  queryParams: WorkingtimeGetQueryParams
): Promise<{ blob: Blob; filename?: string }> => {
  const response = await api.get(`/common/workingtimes/csv`, {
    params: queryParams,
    responseType: 'blob',
  });

  const cd = response.headers['content-disposition'] as string | undefined;
  const nameFromHeader =
    cd?.match(/filename\*=UTF-8''([^;]+)/)?.[1] ||
    cd?.match(/filename="?([^"]+)"?/)?.[1];

  return { blob: response.data as Blob, filename: nameFromHeader };
};

// 原価計算を取得
export const getCostAccountingList = async (
  queryParams: CostAccountingGetQueryParams
): Promise<CostAccountingGetResponse> => {
  const response = await api.get(`/admin/costaccounting`, {
    params: queryParams,
  });
  return response.data;
};

// 原価計算CSV出力
export const exportCostAccountingCsv = async (
  queryParams: CostAccountingGetQueryParams
): Promise<{ blob: Blob; filename?: string }> => {
  const response = await api.get(`/admin/costaccounting/csv`, {
    params: queryParams,
    responseType: 'blob',
  });

  const cd = response.headers['content-disposition'] as string | undefined;
  const nameFromHeader =
    cd?.match(/filename\*=UTF-8''([^;]+)/)?.[1] ||
    cd?.match(/filename="?([^"]+)"?/)?.[1];

  return { blob: response.data as Blob, filename: nameFromHeader };
};

// 原価計算（大連）を取得
export const getCostAccountingDairenList = async (
  queryParams: CostAccountingDairenGetQueryParams
): Promise<CostAccountingDairenGetResponse> => {
  const response = await api.get(`/admin/costaccounting/dairen`, {
    params: queryParams,
  });
  return response.data;
};

// 原価計算（大連）CSV出力
export const exportCostAccountingDairenCsv = async (
  queryParams: CostAccountingDairenGetQueryParams
): Promise<{ blob: Blob; filename?: string }> => {
  const response = await api.get(`/admin/costaccounting/dairen/csv`, {
    params: queryParams,
    responseType: 'blob',
  });

  const cd = response.headers['content-disposition'] as string | undefined;
  const nameFromHeader =
    cd?.match(/filename\*=UTF-8''([^;]+)/)?.[1] ||
    cd?.match(/filename="?([^"]+)"?/)?.[1];

  return { blob: response.data as Blob, filename: nameFromHeader };
};

// プロジェクト名（東京）検索候補取得
export const searchTokyoProjectSuggestions = async (
  q: string
): Promise<Suggestion[]> => {
  const response = await api.get(`/common/autocomplete/projects/tokyo`, {
    params: { q },
  });
  return response.data;
};

// プロジェクト名（大連）検索候補取得
export const searchDairenProjectSuggestions = async (
  q: string
): Promise<Suggestion[]> => {
  const response = await api.get(`/common/autocomplete/projects/dairen`, {
    params: { q },
  });
  return response.data;
};

// 個別単価期間プルダウン取得
export const getUnitPriceYears =
  async (): Promise<UnitPriceYearsGerResponse> => {
    const response = await api.get(`/admin/unitprices/periods`);
    return response.data;
  };

// 個別単価の期間追加
export const createUnitPriceYear = async (year: string): Promise<void> => {
  await api.post(`/admin/unitprices/periods`, { year });
};

// 個別単価CSV出力
export const exportUnitPricesCsv = async (
  queryParams: UnitPriceCsvExportParams
): Promise<{ blob: Blob; filename?: string }> => {
  const response = await api.get(`/admin/unitprices/csv`, {
    params: queryParams,
    responseType: 'blob',
  });

  const cd = response.headers['content-disposition'] as string | undefined;
  const nameFromHeader =
    cd?.match(/filename\*=UTF-8''([^;]+)/)?.[1] ||
    cd?.match(/filename="?([^"]+)"?/)?.[1];

  return { blob: response.data as Blob, filename: nameFromHeader };
};

// 指定した年の個別単価取得
export const getUnitPrices = async (
  yearId: number,
  queryParams: UnitPriceGetQueryParams
): Promise<UnitPriceGetResponse> => {
  const response = await api.get(`/admin/unitprices/${yearId}`, {
    params: queryParams,
  });
  return response.data;
};

// 個別単価更新
export const updateUnitPrices = async (
  yearId: number,
  data: FormattedUnitPricePutRequest
): Promise<void> => {
  await api.put(`/admin/unitprices/${yearId}`, data);
};
